import create from 'zustand';
import { ProjectManifest, ArtifactoryNode } from '../types/artifactory';
import { artifactoryService } from '../services/artifactoryService';

// Define view types for better type safety
export type ViewType = 'blueprint' | 'code' | 'documentation' | 'hydration-plan';

interface ArtifactoryStore {
  manifestId: string | null;
  manifest: ProjectManifest | null;
  selectedNodeId: string | null;
  isLoading: boolean;
  isHydrating: boolean;
  expandedDirs: Set<string>;
  projectName: string;
  projectNameHistory: string[];
  activeView: ViewType; // Add active view state

  setManifestId: (id: string) => void;
  loadManifest: () => Promise<void>;
  selectNode: (nodeId: string) => void;
  updateNodeLocal: (nodeId: string, updates: Partial<ArtifactoryNode>) => void;
  saveNodeConfig: (nodeId: string) => Promise<void>;
  hydrateNode: (nodeId: string, sessionId: string) => Promise<void>;
  refreshManifest: () => Promise<void>;
  resetStore: () => void;
  toggleDir: (path: string) => void;
  expandPathToNode: (nodePath: string) => void;
  setProjectName: (name: string) => void;
}

export const useArtifactoryStore = create<ArtifactoryStore>((set, get) => ({
  manifestId: null,
  manifest: null,
  selectedNodeId: localStorage.getItem('artifactory_selectedNodeId'), // Initialize from localStorage
  isLoading: false,
  isHydrating: false,
  expandedDirs: new Set(['/']) as Set<string>,
  projectName: localStorage.getItem('artifactory_projectName') || 'Untitled Project',
  projectNameHistory: JSON.parse(localStorage.getItem('artifactory_projectNameHistory') || '[]'),
  activeView: localStorage.getItem('artifactory_activeView') as ViewType || 'blueprint', // Initialize from localStorage with default

  setManifestId: (id) => set({ manifestId: id }),
  
  setActiveView: (view: ViewType) => {
    // Save to localStorage
    localStorage.setItem('artifactory_activeView', view);
    set({ activeView: view });
  },

  loadManifest: async () => {
    const { manifestId } = get();
    if (!manifestId) return;

    set({ isLoading: true });
    try {
      const manifest = await artifactoryService.getManifest(manifestId);
      
      // Get the selected node ID from localStorage
      const savedNodeId = localStorage.getItem('artifactory_selectedNodeId');
      
      // Check if the saved node still exists in the manifest
      const nodeStillExists = savedNodeId && manifest.nodes[savedNodeId];
      
      // If the node exists, make sure its path is expanded
      if (nodeStillExists) {
        const nodePath = manifest.nodes[savedNodeId].path;
        get().expandPathToNode(nodePath);
      }
      
      // Extract project name from metadata
      if (manifest && manifest.metadata && manifest.metadata.project_name) {
        get().setProjectName(manifest.metadata.project_name);
      }
      
      set({ 
        manifest, 
        isLoading: false,
        // Only restore the selected node if it still exists in the manifest
        selectedNodeId: nodeStillExists ? savedNodeId : null
      });
    } catch (error) {
      console.error("Failed to load manifest", error);
      set({ isLoading: false });
    }
  },

  selectNode: (nodeId) => {
    // Save to localStorage when a node is selected
    localStorage.setItem('artifactory_selectedNodeId', nodeId);
    set({ selectedNodeId: nodeId });
  },
  
  resetStore: () => {
    // Clear localStorage when resetting the store
    localStorage.removeItem('artifactory_selectedNodeId');
    set({ 
      manifestId: null, 
      manifest: null, 
      selectedNodeId: null, 
      isLoading: false, 
      isHydrating: false,
      expandedDirs: new Set(['/'])
      // Keep project name and history
    });
  },
  
  setProjectName: (name: string) => {
    // Save to localStorage
    localStorage.setItem('artifactory_projectName', name);
    
    // Update history
    const history = get().projectNameHistory;
    const newHistory = [name, ...history.filter(n => n !== name)].slice(0, 5);
    localStorage.setItem('artifactory_projectNameHistory', JSON.stringify(newHistory));
    
    set({ projectName: name, projectNameHistory: newHistory });
  },

  updateNodeLocal: (nodeId, updates) => set(state => {
    if (!state.manifest) return state;
    return {
      manifest: {
        ...state.manifest,
        nodes: {
          ...state.manifest.nodes,
          [nodeId]: { ...state.manifest.nodes[nodeId], ...updates }
        }
      }
    };
  }),

  saveNodeConfig: async (nodeId) => {
    const state = get();
    if (!state.manifest || !state.manifestId) return;
    const node = state.manifest.nodes[nodeId];

    await artifactoryService.updateNode(state.manifestId, nodeId, {
      generation_prompt: node.generation_prompt,
      assigned_persona_id: node.assigned_persona_id,
      dependencies: node.dependencies
    });
  },

  hydrateNode: async (nodeId, sessionId, withDependencies = false) => {
    const { manifestId } = get();
    if (!manifestId) return;

    set({ isHydrating: true });
    try {
      // Update local state to show hydrating status
      get().updateNodeLocal(nodeId, { status: "hydrating" });
      
      // Call the API to hydrate the node
      // console.log(`ArtifactoryStore: Hydrating node ${nodeId} in manifest ${manifestId} ${withDependencies ? 'WITH' : 'WITHOUT'} dependencies`);
      // console.log(`ArtifactoryStore: withDependencies parameter value: ${withDependencies}, type: ${typeof withDependencies}`);
      
      // Ensure withDependencies is a boolean
      const boolWithDependencies = Boolean(withDependencies);
      // console.log(`ArtifactoryStore: Converted withDependencies to boolean: ${boolWithDependencies}`);
      
      const result = await artifactoryService.hydrateNode(manifestId, nodeId, sessionId, boolWithDependencies);
      // console.log(`Hydration result:`, result);
      
      // If successful, update the local state with the artifact ID
      if (result.success && result.artifact_id) {
        // console.log(`Node ${nodeId} hydrated successfully with artifact ID ${result.artifact_id}`);
        get().updateNodeLocal(nodeId, { 
          status: "fresh", 
          artifact_id: result.artifact_id 
        });
      }
      
      // Refresh the manifest to get the latest state from the server
      await get().refreshManifest();
      
      return result;
    } catch (error) {
      console.error(`Error hydrating node ${nodeId}:`, error);
      // Update local state to show error
      get().updateNodeLocal(nodeId, { status: "ghost" });
      throw error;
    } finally {
      set({ isHydrating: false });
    }
  },

  refreshManifest: async () => {
    const { manifestId, selectedNodeId } = get();
    if (!manifestId) return;
    
    set({ isLoading: true });
    try {
      const manifest = await artifactoryService.getManifest(manifestId);
      
      // Preserve the selected node ID if it still exists
      const newSelectedNodeId = manifest.nodes[selectedNodeId] ? selectedNodeId : null;
      
      // If we have a selected node, make sure its path is expanded
      if (newSelectedNodeId && manifest.nodes[newSelectedNodeId]) {
        const nodePath = manifest.nodes[newSelectedNodeId].path;
        get().expandPathToNode(nodePath);
      }
      
      // Update localStorage with the current selected node ID
      if (newSelectedNodeId) {
        localStorage.setItem('artifactory_selectedNodeId', newSelectedNodeId);
      } else {
        localStorage.removeItem('artifactory_selectedNodeId');
      }
      
      set({ 
        manifest,
        selectedNodeId: newSelectedNodeId,
        isLoading: false
      });
    } catch (error) {
      console.error("Failed to refresh manifest:", error);
      set({ isLoading: false });
    }
  },
  
  toggleDir: (path) => set(state => {
    const newExpandedDirs = new Set(state.expandedDirs);
    if (newExpandedDirs.has(path)) {
      newExpandedDirs.delete(path);
    } else {
      newExpandedDirs.add(path);
    }
    return { expandedDirs: newExpandedDirs };
  }),
  
  expandPathToNode: (nodePath) => set(state => {
    if (!nodePath) return state;
    
    const newExpandedDirs = new Set(state.expandedDirs);
    
    // Normalize the path to ensure consistent handling
    const normalizedPath = nodePath.startsWith('/') ? nodePath : `/${nodePath}`;
    
    // Split the path and expand all parent directories
    const parts = normalizedPath.split('/').filter(Boolean);
    let currentPath = '';
    
    // Check if we need to update at all
    let needsUpdate = false;
    
    // Always expand the root directory
    if (!newExpandedDirs.has('/')) {
      newExpandedDirs.add('/');
      needsUpdate = true;
    }
    
    // Expand each directory in the path
    for (let i = 0; i < parts.length; i++) {
      currentPath += '/' + parts[i];
      if (!newExpandedDirs.has(currentPath)) {
        console.log("Expanding directory:", currentPath);
        newExpandedDirs.add(currentPath);
        needsUpdate = true;
      }
    }
    
    // Only return a new state if we actually added directories
    return needsUpdate ? { expandedDirs: newExpandedDirs } : state;
  }),
}));
